import { useCallback, useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Gauge, Zap, Flame, Droplet, Camera, Check, Loader2, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const METERS = [
  { type: "electric", label: "Electric", Icon: Zap, unit: "kWh" },
  { type: "gas", label: "Gas", Icon: Flame, unit: "CCF" },
  { type: "water", label: "Water", Icon: Droplet, unit: "gallons" },
];

const meterImageUrl = (id) => `${API}/meter-readings/${id}/image`;

export function MeterReadings({ propertyId, phase, readOnly = false }) {
  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyType, setBusyType] = useState(null);
  const [confirmFor, setConfirmFor] = useState(null); // reading object being confirmed
  const [value, setValue] = useState("");
  const [unit, setUnit] = useState("");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const pendingType = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/properties/${propertyId}/meter-readings`);
      setReadings(data);
    } catch (e) { console.error("load meter readings failed", e); }
    finally { setLoading(false); }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const byType = (t) => readings.find((r) => r.meter_type === t && r.phase === phase);

  const openPicker = (t) => {
    pendingType.current = t;
    fileRef.current?.click();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const t = pendingType.current;
    if (!file || !t) return;
    setBusyType(t);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("meter_type", t);
      form.append("phase", phase);
      const { data } = await api.post(`/properties/${propertyId}/meter-readings`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await load();
      // Open confirm dialog with the AI-proposed value prefilled (may be empty).
      setConfirmFor(data);
      setValue(data.ai_reading || "");
      setUnit(data.unit || METERS.find((m) => m.type === t)?.unit || "");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't read that photo — please try again.");
    } finally { setBusyType(null); }
  };

  const saveConfirm = async () => {
    if (!confirmFor) return;
    const clean = (value || "").replace(/[^0-9.]/g, "");
    if (!clean) { toast.error("Enter the meter reading."); return; }
    setSaving(true);
    try {
      await api.patch(`/meter-readings/${confirmFor.reading_id}`, {
        user_confirmed_reading: clean, unit: unit || null,
      });
      toast.success("Reading confirmed");
      setConfirmFor(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save the reading.");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/meter-readings/${id}`); await load(); toast.success("Reading removed"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Couldn't remove."); }
  };

  if (loading) return null;

  const phaseLabel = phase === "move_out" ? "move-out" : "move-in";

  return (
    <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-5 text-white" data-testid="meter-readings">
      <div className="flex items-start gap-3">
        <span className="grid place-items-center h-10 w-10 rounded-xl bg-amber/15 text-amber shrink-0"><Gauge size={20} /></span>
        <div className="min-w-0">
          <h3 className="font-heading font-semibold">Utility meter readings</h3>
          <p className="text-sm text-white/60">
            Photograph your electric, gas and water meters at {phaseLabel}. We read the digits — you confirm them — creating dated proof of the values.
          </p>
        </div>
      </div>

      <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" data-testid="meter-file-input" />

      <div className="mt-4 space-y-2.5">
        {METERS.map(({ type, label, Icon, unit: defUnit }) => {
          const r = byType(type);
          const busy = busyType === type;
          return (
            <div key={type} data-testid={`meter-row-${type}`}
              className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-3 py-2.5">
              <span className="grid place-items-center h-9 w-9 rounded-lg bg-white/10 text-white/80 shrink-0"><Icon size={18} /></span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold">{label}</p>
                {r ? (
                  r.confirmed ? (
                    <p className="text-sm text-emerald-300 flex items-center gap-1.5" data-testid={`meter-value-${type}`}>
                      <Check size={13} /> {r.user_confirmed_reading} {r.unit || ""}
                    </p>
                  ) : (
                    <p className="text-xs text-amber-200 flex items-center gap-1.5" data-testid={`meter-unconfirmed-${type}`}>
                      <Sparkles size={12} /> {r.ai_reading ? `AI read ${r.ai_reading}${r.unit ? " " + r.unit : ""} — confirm it` : "Photo saved — enter the value"}
                    </p>
                  )
                ) : (
                  <p className="text-xs text-white/45">Not documented yet</p>
                )}
              </div>

              {r && (
                <a href={meterImageUrl(r.reading_id)} target="_blank" rel="noopener noreferrer"
                  className="hidden sm:block h-11 w-11 rounded-lg overflow-hidden border border-white/10 shrink-0" data-testid={`meter-thumb-${type}`}>
                  <img src={meterImageUrl(r.reading_id)} alt={`${label} meter`} className="h-full w-full object-cover" />
                </a>
              )}

              {readOnly ? (
                r && !r.confirmed && (
                  <span className="text-xs text-white/40">Pending</span>
                )
              ) : (
                <div className="flex items-center gap-1.5 shrink-0">
                  {r && !r.confirmed && (
                    <Button size="sm" data-testid={`meter-confirm-btn-${type}`}
                      onClick={() => { setConfirmFor(r); setValue(r.ai_reading || ""); setUnit(r.unit || defUnit); }}
                      className="rounded-lg h-9 bg-amber hover:bg-amber-hover text-navy font-semibold">
                      Confirm
                    </Button>
                  )}
                  <Button size="sm" variant="outline" disabled={busy}
                    data-testid={`meter-capture-btn-${type}`}
                    onClick={() => openPicker(type)}
                    className="rounded-lg h-9 border-white/20 bg-white/5 text-white hover:bg-white/15">
                    {busy ? <Loader2 size={15} className="animate-spin" /> : <><Camera size={15} className="mr-1.5" />{r ? "Retake" : "Photo"}</>}
                  </Button>
                  {r && (
                    <Button size="sm" variant="ghost" data-testid={`meter-delete-btn-${type}`}
                      onClick={() => remove(r.reading_id)}
                      className="rounded-lg h-9 w-9 p-0 text-[#FCA5A5] hover:bg-[#7F1D1D]/40">
                      <Trash2 size={15} />
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={!!confirmFor} onOpenChange={(o) => { if (!o) setConfirmFor(null); }}>
        <DialogContent data-testid="meter-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirm the meter reading</DialogTitle>
            <DialogDescription>
              {confirmFor?.ai_reading
                ? "We read the value below from your photo. Correct it if needed, then confirm — this becomes your documented reading."
                : "We couldn't read the value automatically. Type the number shown on the meter, then confirm."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Reading</Label>
              <Input data-testid="meter-reading-input" value={value} inputMode="decimal"
                onChange={(e) => setValue(e.target.value)} placeholder="e.g. 04521"
                className="mt-1 h-11 rounded-xl text-lg font-semibold tracking-wide" />
            </div>
            <div>
              <Label className="text-xs">Unit</Label>
              <Input data-testid="meter-unit-input" value={unit}
                onChange={(e) => setUnit(e.target.value)} placeholder="kWh"
                className="mt-1 h-11 rounded-xl" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmFor(null)} className="rounded-xl" data-testid="meter-confirm-cancel">Cancel</Button>
            <Button onClick={saveConfirm} disabled={saving} data-testid="meter-confirm-save"
              className="rounded-xl bg-brand hover:bg-brand-hover text-white">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <><Check size={16} className="mr-1.5" /> Confirm reading</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
